import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'

interface MemoModalProps {
  isOpen: boolean
  onClose: () => void
  /** 확인 시 호출. 입력된 메모(선택, 비어있으면 undefined)를 인자로 전달 */
  onConfirm: (memo?: string) => void
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  isSubmitting?: boolean
  error?: string | null
  memoLabel?: string
  memoPlaceholder?: string
}

/**
 * 수정 저장 시 변경 사유 메모(선택)를 입력받는 모달.
 * 확인을 누르면 입력한 메모를 onConfirm(memo)로 전달한다. 메모는 선택값이라 비워둘 수 있다.
 */
const MemoModal: React.FC<MemoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = '수정 저장',
  message,
  confirmText = '저장',
  cancelText = '취소',
  isSubmitting = false,
  error = null,
  memoLabel = '메모 (선택)',
  memoPlaceholder = '변경 사유 등 메모를 남길 수 있습니다. (선택)'
}) => {
  const [memo, setMemo] = useState('')

  useEffect(() => {
    if (!isOpen) setMemo('')
  }, [isOpen])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleConfirm = () => {
    onConfirm(memo.trim() || undefined)
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded"
        style={{
          width: '400px',
          padding: '20px',
          border: `1px solid ${designTokens.colors.gray[1]}`,
          borderRadius: '4px'
        }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center"
          style={{
            padding: '7px 0',
            borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
            marginBottom: '16px'
          }}
        >
          <h2
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '1.193359375em',
              color: designTokens.colors.gray[6]
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-0 border-0 bg-transparent cursor-pointer"
            style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={12} style={{ color: designTokens.colors.gray[5], strokeWidth: 2 }} />
          </button>
        </div>

        {/* Content */}
        {message && (
          <p
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
              marginBottom: '12px',
              whiteSpace: 'pre-line'
            }}
          >
            {message}
          </p>
        )}

        <div className="flex flex-col gap-2" style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              fontWeight: 600,
              color: designTokens.colors.gray[6]
            }}
          >
            {memoLabel}
          </label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            disabled={isSubmitting}
            placeholder={memoPlaceholder}
            rows={3}
            autoFocus
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[6],
              border: `1px solid ${designTokens.colors.gray[1]}`,
              borderRadius: '4px',
              padding: '8px 10px',
              resize: 'vertical',
              outline: 'none'
            }}
          />
        </div>

        {/* Error */}
        {error && (
          <p
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '13px',
              color: designTokens.colors.error.DEFAULT,
              marginBottom: '12px',
              whiteSpace: 'pre-line'
            }}
          >
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2.5">
          <Button
            size="medium"
            color="light"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '0 10px',
              height: '40px',
              backgroundColor: designTokens.colors.gray[1],
              borderColor: designTokens.colors.gray[1],
              color: designTokens.colors.gray[6]
            }}
          >
            {cancelText}
          </Button>
          <Button
            size="medium"
            color="dark"
            onClick={handleConfirm}
            disabled={isSubmitting}
            style={{
              padding: '0 10px',
              height: '40px',
              backgroundColor: designTokens.colors.primary.dark,
              borderColor: designTokens.colors.primary.dark,
              color: designTokens.colors.white
            }}
          >
            {isSubmitting ? '저장 중...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MemoModal
